import React, {useState, useEffect, useRef, lazy, Suspense, useMemo} from 'react';
import { useFormContext } from '../Provider';
import useValidators from '../Validators/useValidator';
import CommonFieldWrapper from './CommonFieldWrapper';
import Loading, { PageLoading } from '../../Loading';
import { preload } from '../../../Services/helpers';
import Modal from '../../Modal';
import Form, { Actions, Assets } from '..';
import { getAssets } from '../../../Data/Assets';
import { useDocuments } from '../../../Data/Documents';
import { useSession } from '../../Session';
import API from '../../../Services/API';

const Editor = lazy(
    () => import(/* webpackPreload: true, webpackChunkName: "tinymce" */'@tinymce/tinymce-react').then(module => ({ default: module.Editor }))
);

const fallbackValue = '<p>Enter your text here</p>';

let imageResolver = null;

const Wysiwyg = ({label, name, help, onChange, initialValue, disabled, validators, features, mode = 'html', bottomMargin = 50, imageTags, previousTags}) => {
    const { formData, updateField, updateValidity, isValidating, isSubmitting, formDisabled } = useFormContext();
    const value = formatInput(
        formData[name] ?? initialValue ?? fallbackValue,
        mode
    );
    const {isValid, error} = useValidators(value, validators, name, updateValidity);
    const editorRef = useRef(null);
    const [isLoading, setIsLoading] = useState(true);
    const [pickingImage, setPickingImage] = useState(false);
    const {documents} = useDocuments();
    const {hasFeature} = useSession();

    const handleChange = (val) => {
        updateField(name, formatOutput(val, mode));
        onChange && onChange(formatOutput(val, mode));
    }

    const openImageModal = () => {
        setPickingImage(true);
        return new Promise((resolve, reject) => {
            imageResolver = resolve;
        });
    }

    const isDisabled = formDisabled || disabled || isSubmitting;

    useEffect(() => {
        if ((editorRef?.current ?? false) === false) {
            return;
        }

        editorRef.current.contentDocument.body.style.backgroundColor = isDisabled ? '#f3f4f6' : '#FFF';
        editorRef.current.contentDocument.body.style.color = isDisabled ? '#9ca3af' : '#000';

    }, [isDisabled]);

    const opts = useMemo(() => getOpts(features, imageTags), [features, imageTags]);

    // We need to wait for documents to load because we can't change the merge tags after the editor has initialised
    if (hasFeature('DOCUMENTS') && documents === null) {
        return <CommonFieldWrapper {...{label, name, help, value, isValid, isValidating, error, validators, isDisabled, padding: 'px-1 py-1.5'}}>
            <PageLoading short />
        </CommonFieldWrapper>;
    }

    return <CommonFieldWrapper {...{label, name, help, value, isValid, isValidating, error, validators, isDisabled, padding: 'px-1 py-1.5'}}>

        <Suspense fallback={<PageLoading short />}>
            <Editor
                tinymceScriptSrc="/tinymce/tinymce.min.js"
                onInit={(evt, editor) => {
                    setIsLoading(false);
                    editorRef.current = editor;
                }}
                disabled={isDisabled}
                value={value}
                onEditorChange={(newValue, editor) => handleChange(newValue)}
                init={{
                    ...opts,
                    autoresize_bottom_margin: parseInt(bottomMargin),
                    menubar: false,
                    inline_boundaries: false,
                    plugins: opts.plugins,
                    toolbar: opts.toolbar,
                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px; min-height: 25px !important; }',
                    statusbar: false,
                    branding: false,
                    mergetags_prefix: '{{',
                    mergetags_suffix: '}}',
                    mergetags_list: getMergeTags(documents),
                    min_height: 25,
                    height: 25,
                    setup: (editor) => {
                        editor.ui.registry.addButton('soapyImage', {
                            text: null,
                            icon: 'image',
                            onAction: function () {
                                openImageModal().then(imageDetails => {
                                    if (!imageDetails) {
                                        return;
                                    }

                                    editor.insertContent(`<img src="${imageDetails.url}" width="200" />`);
                                });
                            }
                        });
                    }
                }}
            />
        </Suspense>

       {isLoading && <Loading size="2x" className="mb-10" center />}

       <ImagePicker {...{pickingImage, setPickingImage, imageTags, previousTags}} />

    </CommonFieldWrapper>
}

const ImagePicker = ({pickingImage, setPickingImage, imageTags, previousTags}) => {
    const handleSubmit = async (data) => {
        const assets = await getAssets([data.image]);
console.log('assets', assets[0])
        imageResolver({
            url: assets[0].original,
        });
    }

    return <Modal isOpen={pickingImage} setIsOpen={setPickingImage} title="Pick an image" dismissible>
        <Form onSubmit={handleSubmit} closeModalOnSubmit>
            <Assets
                label="Image"
                name="image"
                tags={imageTags}
                previousTags={previousTags}
                single
                showPrevious
            />
            <Actions submit={{label: 'Insert'}} cancel />
        </Form>
    </Modal>
}

const getOpts = (features, imageTags) => {
    const plugins = ['autoresize'];
    const toolbar = [];
    const extraOpts = {};

    preload('/tinymce/tinymce.min.js');
    preload('/tinymce/models/dom/model.min.js');
    preload('/tinymce/icons/default/icons.min.js');
    preload('/tinymce/themes/silver/theme.min.js');

    preload('/tinymce/skins/ui/oxide/skin.min.css');
    preload('/tinymce/skins/ui/oxide/content.min.css');
    preload('/tinymce/skins/content/default/content.min.css');

    // preload('/tinymce/plugins/autolink/plugin.min.js');
    preload('/tinymce/plugins/autoresize/plugin.min.js');

    if (features?.includes('mergetags')) {
        preload('/tinymce/plugins/mergetags/plugin.min.js');
        plugins.push('mergetags');
        toolbar.push('mergetags');
    }

    if (features?.includes('ai')) {
        preload('/tinymce/plugins/ai/plugin.min.js');
        plugins.push('ai');
        toolbar.push('aidialog aishortcuts | ');
        extraOpts.ai_request = aiRequest;
        extraOpts.ai_shortcuts = aiShortcuts;
    }

    if (features?.includes('images')) {
        if (!imageTags) {
            console.warn('You must provide imageTags if you want to use the image picker');
        } else {
            preload('/tinymce/plugins/image/plugin.min.js');
            plugins.push('image');
            toolbar.push('soapyImage');
        }
    }
    if (features?.includes('basicFormatting')) {
        plugins.push('link');
        plugins.push('emoticons');
        toolbar.push('bold italic emoticons link blocks fontsize');
        preload('/tinymce/plugins/link/plugin.min.js');
        preload('/tinymce/plugins/emoticons/plugin.min.js');
        preload('/tinymce/plugins/emoticons/js/emojis.min.js');
    }
    if (features?.includes('moreFormatting')) {
        toolbar.push('alignleft aligncenter alignright | removeformat');
    }
    if (features?.includes('advancedFormatting')) {
        plugins.push('lists');
        toolbar.push('bullist numlist outdent indent');
    }

    return {
        plugins: plugins,
        toolbar: toolbar.join(' '),
        ...extraOpts
    }
}

const aiRequest = (request, respondWith) => {
    return respondWith.string(async () => {
        const response = await API.post('/assistant/wysiwyg', request);
        return response.content;
    });
}

const aiShortcuts = [
    {
        title: 'Touch up grammar',
        prompt: 'Correct this grammar and spelling of this text (British English) but don\'t change it.',
        selection: true
    },
    {
        title: 'Make it more formal',
        prompt: 'formalise-website-content',
        selection: true
    },
    /*
    {
        title: 'Classical',
        subprompts:[
            { title: 'Dialogue', prompt: 'Convert this to a Socratic dialogue.', selection: true },
            { title: 'Homeric', prompt: 'Convert this to a Classical Epic.', selection: true }
        ]
    },
    */
];

const formatInput = (val, mode) => {
    if (mode === 'html') {
        return val;
    } else if (mode === 'singleLine' || mode === 'multiLine') {
        return '<p>'+val.replace(/(\r\n|\n|\r)/gmi, "</p>\n<p>")+'</p>';
    }
}

const formatOutput = (val, mode) => {
    if (mode === 'html') {
        return val;
    } else if (mode === 'singleLine') {
        return val.replace(/(<([^>]+)>)/gmi, "").replace(/(\r\n|\n|\r)+/gmi, " ").trim();
    } else if (mode === 'multiLine') {
        return val.replace(/(<([^>]+)>)/gmi, "\n").replace(/(\r\n|\n|\r)+/gmi, "\n").trim();
    }
}

const sortMergeTags = (a, b) => {
    if (a.title < b.title) {
        return -1;
    }
    if (a.title > b.title) {
        return 1;
    }

    return 0;
}

const getMergeTags = (documents) => {
    const tags = [
        {
            title: 'Business',
            menu: [
                { value: 'business.facebook',           title: 'Business Facebook' },
                { value: 'business.instagram',          title: 'Business Instagram' },
                { value: 'business.name',               title: 'Business Name' },
                { value: 'business.tagline',            title: 'Business Tagline' },
                { value: 'business.phoneNumber',        title: 'Business PhoneNumber' },
                { value: 'business.trustPilot',         title: 'Business TrustPilot' },
                { value: 'business.twitter',            title: 'Business Twitter' },
                { value: 'business.website',            title: 'Business Website' },
                { value: 'business.whatsApp',           title: 'Business WhatsApp' },
                { value: 'business.mastodon',           title: 'Business Mastodon' },
                { value: 'business.threads',            title: 'Business Threads' },
                { value: 'business.youtube',            title: 'Business YouTube' },
                { value: 'business.google',             title: 'Business Google' },
            ].sort(sortMergeTags)
        },
        {
            title: 'Customer',
            menu: [
                { value: 'customer.balance',            title: 'Customer Balance' },
                { value: 'customer.balanceStatus',      title: 'Customer Balance Status' },
                { value: 'customer.email',              title: 'Customer Email' },
                { value: 'customer.firstName',          title: 'Customer First Name' },
                { value: 'customer.lastName',           title: 'Customer Last Name' },
                { value: 'customer.name',               title: 'Customer Full Name' },
                { value: 'customer.businessName',       title: 'Customer Business Name' },
                { value: 'customer.autoName',           title: 'Customer Auto Name' },
                { value: 'customer.type',               title: 'Customer Type' },
                { value: 'customer.phoneNumber',        title: 'Customer Phone Number' },
                { value: 'customer.address',            title: 'Customer Address (Full)' },
                { value: 'customer.addressFirstLine',   title: 'Customer Address (First Line)' },
                { value: 'customer.addressTown',        title: 'Customer Address (Town)' },
                { value: 'customer.addressPostcode',    title: 'Customer Address (Postcode)' },
                { value: 'customer.addressCountry',     title: 'Customer Address (Country)' },
                { value: 'customer.addressStreetName',  title: 'Customer Address (Street Name)' },
                { value: 'customer.postcode',           title: 'Customer Postcode' },
                { value: 'customer.shortId',            title: 'Customer Short ID' },
                { value: 'customer.status',             title: 'Customer Status' },
                { value: 'customer.pin',                title: 'Customer Pin' },
            ].sort(sortMergeTags)
        },
        {
            title: 'Invoice',
            menu: [
                { value: 'invoice.total',               title: 'Invoice Total' },
                { value: 'invoice.outstanding',         title: 'Invoice Outstanding' },
                { value: 'invoice.paid',                title: 'Invoice Paid' },
                { value: 'invoice.status',              title: 'Invoice Status' },
                { value: 'invoice.dueDate',             title: 'Invoice Due Date' },
            ].sort(sortMergeTags)
        },
        {
            title: 'Job',
            menu: [
                { value: 'job.cost',                    title: 'Job Cost' },
                { value: 'job.dueWeek',                 title: 'Job Due Week' },
                { value: 'job.frequency',               title: 'Job Frequency' },
                { value: 'job.lastDone',                title: 'Job LastDone' },
                { value: 'job.notes',                   title: 'Job Notes' },
                { value: 'job.scheduled',               title: 'Job Scheduled' },
                { value: 'job.scheduledDate',           title: 'Job Scheduled Date' },
                { value: 'job.estimatedTime',           title: 'Job Estimated Time' },
                { value: 'job.services',                title: 'Job Services' },
                { value: 'job.shortId',                 title: 'Job Short ID' },
                { value: 'job.status',                  title: 'Job Status' },
            ].sort(sortMergeTags)
        },
        {
            title: 'Quote',
            menu: [
                { value: 'quote.name',                  title: 'Quote Full Name' },
                { value: 'quote.firstName',             title: 'Quote First Name' },
                { value: 'quote.lastName',              title: 'Quote Last Name' },
                { value: 'quote.status',                title: 'Quote Status' },
                { value: 'quote.address',               title: 'Quote Address (Full)' },
                { value: 'quote.addressFirstLine',      title: 'Quote Address (First Line)' },
                { value: 'quote.addressTown',           title: 'Quote Address (Town)' },
                { value: 'quote.addressPostcode',       title: 'Quote Address (Postcode)' },
                { value: 'quote.addressCountry',        title: 'Quote Address (Country)' },
                { value: 'quote.addressStreetName',     title: 'Quote Address (Street Name)' },
            ].sort(sortMergeTags)
        },
        {
            title: 'User',
            menu: [
                { value: 'user.email',                  title: 'User Email' },
                { value: 'user.name',                   title: 'User Full Name' },
                { value: 'user.firstName',              title: 'User First Name' },
                { value: 'user.lastName',               title: 'User Last Name' },
            ].sort(sortMergeTags)
        },
        {
            title: 'Link',
            menu: [
                { value: 'link',                        title: 'Link' },
                { value: 'link.short',                  title: 'Short link' },
            ].sort(sortMergeTags)
        },
    ];

    if (documents && documents.length > 0) {
        tags.push({
            title: 'Document',
            menu: [
                // { value: 'document.linked',               title: 'Linked documents' },
                ...documents.map(d => ({
                    value: `document.${d.key}`,
                    title: d.title,
                }))
            ].sort(sortMergeTags)
        });
    }

    return tags.sort(sortMergeTags);
}

export default Wysiwyg;
